import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AssetCard = ({ asset, onPreview, onDownload }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await onDownload(asset);
      setTimeout(() => setIsDownloading(false), 1500);
    } catch (error) {
      console.error('Download failed:', error);
      setIsDownloading(false);
    }
  };

  const handleLinkClick = () => {
    if (asset?.assetType === 'link' && asset?.linkUrl) {
      window.open(asset?.linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const getFileTypeIcon = (type) => {
    const iconMap = {
      'image': 'Image',
      'video': 'Video',
      'pdf': 'FileText',
      'svg': 'FileCode',
      'link': 'Link'
    };
    return iconMap?.[type] || 'File';
  };

  const getFileTypeColor = (type) => {
    const colorMap = {
      'image': 'text-blue-600',
      'video': 'text-purple-600',
      'pdf': 'text-red-600',
      'svg': 'text-green-600',
      'link': 'text-orange-600'
    };
    return colorMap?.[type] || 'text-gray-600';
  };

  const getDomainIcon = (domain) => {
    if (!domain) return 'Link';
    const lowerDomain = domain?.toLowerCase();
    
    if (lowerDomain?.includes('youtube.com') || lowerDomain?.includes('youtu.be')) {
      return 'Youtube';
    }
    if (lowerDomain?.includes('twitter.com') || lowerDomain?.includes('x.com')) {
      return 'Twitter';
    }
    if (lowerDomain?.includes('facebook.com') || lowerDomain?.includes('fb.com')) {
      return 'Facebook';
    }
    if (lowerDomain?.includes('instagram.com')) {
      return 'Instagram';
    }
    if (lowerDomain?.includes('linkedin.com')) {
      return 'Linkedin';
    }
    if (lowerDomain?.includes('github.com')) {
      return 'Github';
    }
    
    return 'Link';
  };

  const getDomainColor = (domain) => {
    if (!domain) return 'text-orange-600';
    const lowerDomain = domain?.toLowerCase();
    
    if (lowerDomain?.includes('youtube.com') || lowerDomain?.includes('youtu.be')) {
      return 'text-red-600';
    }
    if (lowerDomain?.includes('twitter.com') || lowerDomain?.includes('x.com')) {
      return 'text-blue-500';
    }
    if (lowerDomain?.includes('facebook.com') || lowerDomain?.includes('fb.com')) {
      return 'text-blue-600';
    }
    if (lowerDomain?.includes('instagram.com')) {
      return 'text-pink-600';
    }
    if (lowerDomain?.includes('linkedin.com')) {
      return 'text-blue-700';
    }
    if (lowerDomain?.includes('github.com')) {
      return 'text-gray-800';
    }
    
    return 'text-orange-600';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-250 group">
      <div className="relative aspect-[4/3] bg-muted overflow-hidden cursor-pointer" onClick={() => onPreview(asset)}>
        {asset?.assetType === 'link' ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/50">
            <div className={`mb-4 ${getDomainColor(asset?.linkDomain)}`}>
              <Icon name={getDomainIcon(asset?.linkDomain)} size={64} />
            </div>
            <div className="px-4 text-center">
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Icon name="Globe" size={14} />
                <span className="truncate max-w-[200px]">{asset?.linkDomain || 'External Link'}</span>
              </div>
            </div>
          </div>
        ) : (
          <Image
            src={asset?.thumbnail || asset?.fullImage}
            alt={asset?.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-250"
          />
        )}
        <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity duration-250 flex items-center justify-center">
          <div className="bg-card rounded-full p-3 shadow-lg">
            <Icon name="Eye" size={24} color="var(--color-primary)" />
          </div>
        </div>
        <div className={`absolute top-3 right-3 bg-card rounded-full px-3 py-1.5 shadow-md flex items-center gap-2 ${getFileTypeColor(asset?.fileType)}`}>
          <Icon name={getFileTypeIcon(asset?.fileType)} size={14} />
          <span className="text-xs font-medium uppercase">{asset?.format}</span>
        </div>
      </div>
      <div className="p-4 md:p-5">
        <h3 className="text-base md:text-lg font-semibold text-foreground mb-2 line-clamp-2">
          {asset?.title}
        </h3>

        <div className="flex flex-wrap items-center gap-3 mb-4 text-xs md:text-sm text-muted-foreground">
          {asset?.dimensions && (
            <div className="flex items-center gap-1.5">
              <Icon name="Maximize2" size={14} />
              <span className="whitespace-nowrap">{asset?.dimensions}</span>
            </div>
          )}
          {asset?.campaign && (
            <div className="flex items-center gap-1.5">
              <Icon name="Tag" size={14} />
              <span className="line-clamp-1">{asset?.campaign}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {asset?.assetType === 'link' ? (
            <Button
              variant="default"
              size="sm"
              fullWidth
              onClick={handleLinkClick}
              iconName="ExternalLink"
              iconPosition="left"
            >
              Visit Link
            </Button>
          ) : (
            <Button
              variant="default"
              size="sm"
              fullWidth
              onClick={handleDownload}
              loading={isDownloading}
              disabled={isDownloading}
              iconName={isDownloading ? 'Check' : 'Download'}
              iconPosition="left"
            >
              {isDownloading ? 'Downloaded!' : 'Download'}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPreview(asset)}
            iconName="Eye"
            className="flex-shrink-0"
          />
        </div>
      </div>
    </div>
  );
};

export default AssetCard;